// The eight faces of the die for this match, as a row of round squares. Both
// players roll this wheel. A face shows its icon on its polarity color, with no number and no words. A click on
// a face opens it as a full card: on top of the log in the wide layout, over
// the page in the compact layout. While the die spins, a
// highlight hops along the row, wraps, and stops on the face the die landed on.

import { DIE_SPIN_MS, WHEEL_CYCLE_HOPS, WHEEL_CYCLE_SLOWDOWN } from '../constants';
import type { GameState } from '../game/state';
import { el } from '../lib/dom';
import { hopSchedule } from '../lib/hopSchedule';
import { playSound } from '../sound/player';
import { spellById, spellClasses } from '../spells/library';
import { gameStore } from '../state/game';
import { layoutStore } from '../state/layout';
import { type StageState, stageStore } from '../state/stage';
import { showWheelFace } from '../state/tray';
import { setWheelPick, wheelPickStore } from '../state/wheelPick';
import { createSpellIcon } from './SpellIcon';
import { createWheelCardModal } from './WheelCardModal';

export const createWheel = (): HTMLElement => {
  const ring = el('ol', { className: 'wheel' });

  let faces: HTMLButtonElement[] = [];
  let drawnWheel: readonly string[] = [];
  let drawnWheelWasFull = false;
  let selected: number | null = null;
  let cycledSerial = 0;
  let cycleTimers: ReturnType<typeof setTimeout>[] = [];

  const markSelected = (index: number | null): void => {
    selected = index;
    faces.forEach((face, faceIndex) => {
      const isSelected = faceIndex === selected;
      face.classList.toggle('wheel__face--selected', isSelected);
      face.ariaPressed = String(isSelected);
    });
  };

  const modal = createWheelCardModal(() => markSelected(null));
  const root = el('div', { className: 'wheel-view' }, [ring, modal.root]);

  // The wide layout has room under the wheel, and the card rests on the log there.
  // The compact layout has the wheel and the spell on two tabs, so the card comes over the page.
  const openFace = (index: number): void => {
    const spellId = drawnWheel[index];
    if (spellId === undefined) return;
    if (layoutStore.get() === 'wide') {
      showWheelFace(index);
      return;
    }
    markSelected(index);
    modal.open(spellId);
  };

  const buildFaces = (state: GameState): void => {
    drawnWheel = state.wheel;
    faces = state.wheel.map((spellId, index) => {
      const spell = spellById(spellId);
      return el(
        'button',
        {
          className: `wheel__face ${spellClasses('wheel__face', spell)}`,
          type: 'button',
          ariaLabel: `Face ${index + 1}, ${spell.name}`,
          onclick: () => openFace(index),
        },
        [createSpellIcon(spell.id, 'wheel__glyph')],
      );
    });
    ring.replaceChildren(...faces.map((face) => el('li', { className: 'wheel__slot' }, [face])));
    // The faces are new, so an open card may show a face that is gone.
    modal.close();
    setWheelPick(null);
    markSelected(null);
  };

  const mark = (className: string, index: number | null): void => {
    faces.forEach((face, faceIndex) => {
      face.classList.toggle(className, faceIndex === index);
    });
  };

  const stopCycle = (): void => {
    for (const timer of cycleTimers) clearTimeout(timer);
    cycleTimers = [];
    ring.classList.remove('wheel--rolling');
    mark('wheel__face--candidate', null);
  };

  const startCycle = (face: number): void => {
    stopCycle();
    mark('wheel__face--lit', null);
    ring.classList.add('wheel--rolling');
    const hops = hopSchedule(
      face,
      faces.length,
      WHEEL_CYCLE_HOPS,
      DIE_SPIN_MS,
      WHEEL_CYCLE_SLOWDOWN,
    );
    // Each hop ticks. The last one, on the rolled face, has its own sound.
    cycleTimers = hops.map((hop, count) =>
      setTimeout(() => {
        mark('wheel__face--candidate', hop.index);
        if (count === hops.length - 1) playSound('wheelStop');
        else playSound('wheelHop', 0, count);
      }, hop.atMs),
    );
  };

  const drawGame = (state: GameState): void => {
    if (state.wheel !== drawnWheel) {
      // The one face that changed flips. A whole new wheel does not.
      const changed = state.wheel.map((id, index) => (id !== drawnWheel[index] ? index : -1));
      const fresh = changed.filter((index) => index !== -1);
      stopCycle();
      buildFaces(state);
      if (fresh.length === 1 && drawnWheelWasFull) {
        faces[fresh[0] as number]?.classList.add('wheel__face--fresh');
      }
      drawnWheelWasFull = state.wheel.length > 0;
    }
    if (!state.lastRoll) cycledSerial = 0;
  };

  // The stage owns the roll moment. The ring follows it.
  const drawStage = (stage: StageState): void => {
    const roll = stage.roll;
    // A match with casters has no wheel, and its roll is for energy.
    if (!roll || faces.length === 0) {
      stopCycle();
      mark('wheel__face--lit', null);
      return;
    }
    if (stage.phase === 'spinning') {
      if (roll.serial === cycledSerial) return;
      cycledSerial = roll.serial;
      startCycle(roll.face);
      return;
    }
    if (stage.phase === 'landed') {
      stopCycle();
      mark('wheel__face--lit', roll.face);
    }
  };

  drawGame(gameStore.get());
  gameStore.subscribe(drawGame);
  stageStore.subscribe(drawStage);
  wheelPickStore.subscribe(markSelected);
  return root;
};
